import { useState } from "react";
import { FileText, Upload, Eye, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openBlob, useApi } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DataState, ConfirmDialog, NativeSelect, Badge } from "./common";
import { fmtDate } from "../lib/format";

const CATS = {
  worker: ["Attestato", "Patentino", "Idoneità", "Documento"],
  site: ["PSC", "POS", "Contratto", "Documento"],
  vehicle: ["Libretto", "Assicurazione", "Revisione", "Documento"],
};

export function DocRow({ d, canDelete, onDelete, showEntity }) {
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`doc-row-${d.id}`}>
      <div className="h-10 w-10 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-sky-300" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{d.name}</div>
        <div className="text-xs text-muted-foreground truncate flex items-center gap-2">
          <Badge className="bg-slate-800 border-slate-600 text-slate-200">{d.category}</Badge>
          {showEntity && <span className="truncate">{d.entity_name}</span>}
          <span>{fmtDate(d.created_at)}</span>
        </div>
      </div>
      <Button size="icon" variant="ghost" onClick={() => openBlob(`/documents/${d.id}/download`).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-view-${d.id}`} aria-label="Visualizza"><Eye className="h-4 w-4" /></Button>
      {canDelete && <Button size="icon" variant="ghost" className="text-red-400" onClick={() => onDelete(d)} data-testid={`doc-delete-${d.id}`} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>}
    </li>
  );
}

export function useDocumentDelete(reload) {
  const [del, setDel] = useState(null);
  const remove = async () => {
    try {
      await api.delete(`/documents/${del.id}`);
      toast.success("✓ Documento eliminato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };
  return { del, setDel, remove };
}

function useDocumentUpload(entityType, entityId, reload) {
  const [cat, setCat] = useState(CATS[entityType][0]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return toast.error("Seleziona un file");
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("category", cat);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("✓ Documento caricato");
      setFile(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { cat, setCat, file, setFile, busy, upload };
}

function UploadBar({ entityType, u }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-4 p-3 rounded-lg border border-dashed border-border">
      <NativeSelect value={u.cat} onChange={(e) => u.setCat(e.target.value)} className="sm:w-44" data-testid="doc-category-select">
        {CATS[entityType].map((c) => <option key={c}>{c}</option>)}
      </NativeSelect>
      <Input type="file" key={u.file ? "f" : "e"} onChange={(e) => u.setFile(e.target.files[0])} data-testid="doc-file-input" className="flex-1" />
      <Button onClick={u.upload} disabled={u.busy} data-testid="doc-upload-btn">{u.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Carica</Button>
    </div>
  );
}

export default function DocumentsPanel({ entityType, entityId, canEdit }) {
  const { data, loading, reload } = useApi("/documents", { entity_type: entityType, entity_id: entityId });
  const u = useDocumentUpload(entityType, entityId, reload);
  const { del, setDel, remove } = useDocumentDelete(reload);
  return (
    <div data-testid="documents-panel">
      {canEdit && <UploadBar entityType={entityType} u={u} />}
      <DataState loading={loading} empty={!data?.items?.length} emptyTitle="Nessun documento">
        <ul className="divide-y divide-border/60">
          {(data?.items || []).map((d) => <DocRow key={d.id} d={d} canDelete={canEdit} onDelete={setDel} />)}
        </ul>
      </DataState>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Eliminare il documento?" description={del?.name} onConfirm={remove} />
    </div>
  );
}
